import { useEffect, useState } from "react";
import { fetchFamilyPreview, fetchFamilyPreviewFlag, fetchPlan, loadStoredSession, savePlan } from "@/pages/beta/courseFitApi";
import { fastFitParams, readDraft, readGuideCtx, saveDraft, workingNames } from "@/lib/fastFitGuideContext";

const CAP = 3;

// Fast Fit context for a guide page: only when arriving with ?from=fast-fit, a stored context, and the server gate on.
export function useFastFitGuide(search, slug) {
  const params = fastFitParams(search);
  const ctx = params ? readGuideCtx() : null;
  const [state, setState] = useState({ status: params && ctx?.sessionId ? "loading" : "off" });
  const sid = ctx?.sessionId;

  useEffect(() => {
    if (!params || !sid) { setState({ status: "off" }); return undefined; }
    let live = true;
    (async () => {
      const flag = await fetchFamilyPreviewFlag();
      if (!flag.ok || !flag.data?.enabled) { if (live) setState({ status: "off" }); return; }
      const stored = loadStoredSession();
      const token = stored?.sessionId === sid ? stored.accessToken : null;
      const [v, p] = await Promise.all([fetchFamilyPreview(sid, token), fetchPlan(sid, token)]);
      if (!live) return;
      if (!v.ok || v.data.snapshotId !== ctx.snapshotId) { setState({ status: "stale" }); return; }
      const names = workingNames(readDraft(ctx.snapshotId), p.ok ? p.data.plan : null)?.names || [];
      setState({ status: "ready", view: v.data, plan: p.ok ? p.data.plan : null, names, token, ctx });
    })();
    return () => { live = false; };
  }, [sid, !!params]); // eslint-disable-line react-hooks/exhaustive-deps

  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMsg(null); }, [search, slug]);
  const change = async (name, add) => {
    if (state.status !== "ready") return;
    const next = add ? [...state.names, name] : state.names.filter((n) => n !== name);
    setMsg(null);
    if (!next.length) { setMsg("Your plan needs at least one subject. Add another before removing this one."); return; }
    if (next.length > CAP) { setMsg("Your plan already has 3 subjects. Remove one first."); return; }
    setBusy(true);
    const r = await savePlan(state.ctx.sessionId, state.token, { mode: state.plan?.mode || "primary", interests: next });
    setBusy(false);
    if (!r.ok) {
      setMsg(r.status === 422 ? "Some subjects in your selection need reviewing on your results page first." : "We couldn't update your plan. Nothing was changed.");
      return;
    }
    saveDraft(state.ctx.snapshotId, next, r.data.plan?.updatedAt);
    setState((s) => ({ ...s, names: next, plan: r.data.plan }));
    setMsg(add ? `${name} added to your plan.` : `${name} removed from your plan.`);
  };
  // No context in this URL means off now, even before the effect resets state (prevents a stale 'ready' render).
  return { ...(params && ctx?.sessionId ? state : { status: "off" }), params, change, msg, busy, cap: CAP };
}
